import React, { useState, useEffect } from 'react';
import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import AVSearch from './components/AVSearch';
import Settings from './components/Settings';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5011';
const REACT_APP_GOOGLE_REDIRECTURI = process.env.REACT_APP_GOOGLE_REDIRECTURI || 'http://localhost:5011/api/google-auth';

const theme = createTheme({
  palette: {
    primary: {
      main: 'rgb(142, 36, 170)', // Your desired button color
    },
  },
});

// Utility function for checking existing session
const checkExistingSession = async (backendUrl, setIsLoggedIn, setUser, setIsLoading) => {
  try {
    const response = await fetch(`${backendUrl}/api/protected`, { credentials: 'include' });
    if (response.ok) {
      const data = await response.json();
      setUser({ ...data.user });
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  } catch (error) {
    console.error('Error checking existing session:', error);
    setIsLoggedIn(false);
  } finally {
    setIsLoading(false);
  }
};

function App() {
  const [message, setMessage] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null); // For the user menu
  const [selectedMenuItem, setSelectedMenuItem] = useState('Search'); // Default to Search
  const [menuAnchorEl, setMenuAnchorEl] = useState(null); // Anchor for the main menu



  const open = Boolean(anchorEl);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (item) => {
    setSelectedMenuItem(item);
    handleMenuClose();
  };

  useEffect(() => {
    const fetchMessage = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/message`, { credentials: 'include' });
        const data = await response.json();
        setMessage(data.message);
      } catch (error) {
        console.error('Error fetching message:', error);
        setMessage('Error fetching message');
      }
    };

    fetchMessage();
  }, []);

  useEffect(() => {
    checkExistingSession(backendUrl, setIsLoggedIn, setUser, setIsLoading);
  }, []);

  // Google oauth
  // Step 1
  // send user to Google Login prompt and 
  // return from Frontend Google Login  to /api/google-auth in the backend as a secure POST, not as a GET
  // post with the tokenResponse.code is much more secure than get, as the code will not appear in the URL, proxys etc.
  // The return code of the POST /api/google-auth tells the frontend that the use is logged in.

  const login = useGoogleLogin({
    scope: 'openid profile email https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive.appdata https://www.googleapis.com/auth/gmail.readonly',
    flow: 'auth-code', // <-- required to get id_token
    // prompt: 'consent', // to always get refresh_token
    redirectUri: REACT_APP_GOOGLE_REDIRECTURI,

    onSuccess: async (tokenResponse) => {
      console.log("Received token response from Google tokenResponse.code:", tokenResponse.code);
  
      try {
        const backendResponse = await fetch(REACT_APP_GOOGLE_REDIRECTURI, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: tokenResponse.code }),
          credentials: 'include',
        });
  
        if (backendResponse.ok) {
          const data = await backendResponse.json();
          setUser(data.user);
          setIsLoggedIn(true);
          console.log("Logged in as:", data.user);
        } else {
          console.error('Error sending token to backend');
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('Error sending token to backend:', error);
        setIsLoggedIn(false);
      }
    },
    onError: (errorResponse) => {
      console.error('Login Failure:', errorResponse);
    },
  });
  


  const handleLogoutSuccess = async () => {
    console.log('Logout Success');
    try {
      await fetch(`${backendUrl}/api/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setIsLoggedIn(false);
      setUser(null);
      googleLogout();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleProtected = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/protected`, { credentials: 'include' });
      const data = await response.json();
      console.log('Protected message:', data.message);
    } catch (error) {
      console.error('Error fetching protected message:', error);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh',  }}>
        <AppBar position="static" sx={{ backgroundColor: 'white' }}>
          <Toolbar>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2, color: 'black' }} // Changed color to black
              onClick={handleMenuClick}
              aria-controls={isMenuOpen ? 'main-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isMenuOpen ? 'true' : undefined}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="main-menu"
              anchorEl={menuAnchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem onClick={() => handleMenuItemClick('Search')}>Search</MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('Settings')}>Settings</MenuItem>
            </Menu>
            <Typography variant="h6" component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', color: 'black' }}>
              <span>AV Search</span>
              {isLoggedIn && user && <span>{user.name}</span>}
            </Typography>
            {isLoggedIn && user && (
              <div>
                <IconButton
                  onClick={handleUserMenuClick}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <Avatar alt={user.name} src={user.picture} sx={{ width: 40, height: 40 }} />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  id="account-menu"
                  open={open}
                  onClose={handleUserMenuClose}
                  onClick={handleUserMenuClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={handleLogoutSuccess}>
                    Logout
                  </MenuItem>
                </Menu>
              </div>
            )}
          </Toolbar>
        </AppBar>
        <Container component="main" sx={{ mt: 2, mb: 2, maxWidth: '100%', width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {selectedMenuItem}
          </Typography>
          {isLoggedIn ? (
            user ? (
              <>
                {selectedMenuItem === 'Search' && <AVSearch />}
                {selectedMenuItem === 'Settings' && <Settings message={message} handleProtected={handleProtected} />}
              </>
            ) : (
              <Typography>Loading user data...</Typography>
            )
          ) : (
            <Button variant="contained" onClick={() => login()}>
              Login with Google
            </Button>

          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
